import React from "react";
import { useMsal } from "@azure/msal-react";
import { useParams } from "react-router-dom";
import { getToken } from "../api/auth";
import {
  ToolGroup,
  getToolGroups,
  addToolGroup,
  deleteToolGroup,
} from "../api/toolGroupsApi";

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function errorMessageFromAxios(err: any, fallback: string): string {
  const status = err?.response?.status;
  const body = err?.response?.data;
  const serverMsg = typeof body === "object" && body !== null ? body.error : null;
  if (status === 401 || status === 403) {
    return "You don't have access to this tool's roster. The Tool Owner of this tool can manage its groups.";
  }
  if (status === 404 && serverMsg) return serverMsg;       // tool not found, group not found in Entra
  if (status === 400 && serverMsg) return serverMsg;       // bad GUID format
  if (status === 409 && serverMsg) return serverMsg;       // duplicate group
  if (serverMsg) return serverMsg;
  return fallback;
}

const GroupsRosterPage: React.FC = () => {
  const { instance, accounts } = useMsal();
  const { toolName } = useParams<{ toolName: string }>();
  const account = accounts[0];

  const [groups,    setGroups]    = React.useState<ToolGroup[]>([]);
  const [loading,   setLoading]   = React.useState(true);
  const [error,     setError]     = React.useState<string | null>(null);

  const [newGuid,   setNewGuid]   = React.useState("");
  const [adding,    setAdding]    = React.useState(false);
  const [addError,  setAddError]  = React.useState<string | null>(null);

  const [deletingId, setDeletingId] = React.useState<number | null>(null);

  // ── Initial load ──────────────────────────────────────────────────────────
  React.useEffect(() => {
    if (!toolName || !account) return;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        await getToken(instance, account);
        const fetched = await getToolGroups(toolName!);
        setGroups(fetched);
      } catch (err) {
        setError(errorMessageFromAxios(err, "Failed to load roster. Please refresh the page."));
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [toolName, account, instance]);

  // ── Add a group ───────────────────────────────────────────────────────────
  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    setAddError(null);

    const trimmed = newGuid.trim();
    if (!trimmed) {
      setAddError("Paste a group Object ID (GUID) to add.");
      return;
    }
    if (!GUID_RE.test(trimmed)) {
      setAddError("That doesn't look like a valid GUID. Format: 8-4-4-4-12 hex characters.");
      return;
    }
    // Client-side dedupe — server enforces this too, but a fast UI check avoids a round trip
    if (groups.some((g) => g.groupObjectId.toLowerCase() === trimmed.toLowerCase())) {
      setAddError("That group is already in this tool's roster.");
      return;
    }

    setAdding(true);
    try {
      await addToolGroup(toolName!, trimmed);
      // Re-fetch rather than appending the single returned row: adding a group
      // also auto-adds any groups nested inside it, and those must appear too.
      const refreshed = await getToolGroups(toolName!);
      setGroups(refreshed);
      setNewGuid("");
    } catch (err) {
      setAddError(errorMessageFromAxios(err, "Failed to add the group. Please try again."));
      console.error(err);
    } finally {
      setAdding(false);
    }
  }

  // ── Delete a group ────────────────────────────────────────────────────────
  async function handleDelete(group: ToolGroup) {
    const ok = window.confirm(
      `Remove "${group.groupName}" from ${toolName}'s roster?\n\n` +
      `This will only stop future reviews from including this group. ` +
      `It does NOT remove the group from Entra or change anyone's actual access.`
    );
    if (!ok) return;

    setDeletingId(group.id);
    try {
      await deleteToolGroup(toolName!, group.id);
      setGroups((prev) => prev.filter((g) => g.id !== group.id));
    } catch (err) {
      window.alert(errorMessageFromAxios(err, "Failed to remove the group. Please try again."));
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  if (!toolName) {
    return (
      <div style={pageStyle}>
        <h2>Invalid link</h2>
        <p>This page needs a tool name in the URL (e.g. <code>/tools/SampleApp/groups</code>).</p>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={headerStyle}>
        <div>
          <h2 style={{ margin: 0 }}>{toolName} — Groups Roster</h2>
          <p style={{ margin: "6px 0 0", fontSize: "13px", opacity: 0.92 }}>
            Manage the Entra security groups whose membership will be reviewed on each cycle.
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginLeft: 32 }}>
          <img src="/trivago-logo.png" alt="trivago" style={{ height: 36 }} />
          <span style={{ fontSize: 11, letterSpacing: "0.08em", opacity: 0.85, marginTop: 4 }}>IT Operations</span>
        </div>
      </div>

      {/* Info bar */}
      <div style={infoBarStyle}>
        <span style={{ fontSize: 16 }}>ℹ️</span>
        <span>
          Paste an Entra group Object ID to add it. The display name is filled in automatically.
          Removing a group here only stops future reviews from including it — it does not change any access.
        </span>
      </div>

      {loading ? (
        <p>Loading roster…</p>
      ) : error ? (
        <div style={errorBoxStyle}>{error}</div>
      ) : (
        <>
          {/* Current groups table */}
          <h3 style={sectionHeadingStyle}>
            Current groups
            <span style={countBadgeStyle}>{groups.length}</span>
          </h3>

          {groups.length === 0 ? (
            <p style={{ color: "#605e5c", fontStyle: "italic" }}>
              No groups configured yet. Add one below to include it in the next review cycle.
            </p>
          ) : (
            <table style={tableStyle}>
              <colgroup>
                <col style={{ width: "38%" }} />
                <col style={{ width: "47%" }} />
                <col style={{ width: "15%" }} />
              </colgroup>
              <thead>
                <tr style={{ background: "#f3f3f3", textAlign: "left" }}>
                  <th style={thStyle}>Group Object ID</th>
                  <th style={thStyle}>Group Name (from Entra)</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <tr key={g.id} style={{ borderBottom: "1px solid #eee" }}>
                    <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: 12.5 }}>{g.groupObjectId}</td>
                    <td style={tdStyle}>{g.groupName}</td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => handleDelete(g)}
                        disabled={deletingId === g.id}
                        style={{
                          ...btnStyle,
                          background: "#d13438",
                          opacity: deletingId === g.id ? 0.6 : 1,
                        }}
                      >
                        {deletingId === g.id ? "Removing…" : "Remove"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Add a group */}
          <h3 style={{ ...sectionHeadingStyle, marginTop: 40 }}>Add a group</h3>
          <form onSubmit={handleAdd} style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
            <input
              type="text"
              value={newGuid}
              onChange={(e) => setNewGuid(e.target.value)}
              placeholder="Paste the group Object ID (GUID)"
              disabled={adding}
              style={{
                flex: 1,
                padding: "10px 12px",
                fontSize: 14,
                fontFamily: "monospace",
                border: "1px solid #ccc",
                borderRadius: 4,
              }}
            />
            <button
              type="submit"
              disabled={adding}
              style={{
                ...btnStyle,
                background: "#0078d4",
                padding: "10px 24px",
                fontSize: 14,
                opacity: adding ? 0.6 : 1,
              }}
            >
              {adding ? "Adding…" : "Add"}
            </button>
          </form>

          {addError && <div style={{ ...errorBoxStyle, marginTop: 12 }}>{addError}</div>}
        </>
      )}
    </div>
  );
};

// ── Styles ──────────────────────────────────────────────────────────────────
const pageStyle: React.CSSProperties = {
  padding: 24,
  fontFamily: "Segoe UI, sans-serif",
  maxWidth: 1100,
  margin: "0 auto",
};

const headerStyle: React.CSSProperties = {
  background: "#0078d4",
  color: "white",
  padding: "16px 24px",
  marginBottom: 24,
  borderRadius: 4,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const infoBarStyle: React.CSSProperties = {
  background: "#eff6fc",
  border: "1px solid #c7e0f4",
  borderRadius: 4,
  padding: "10px 16px",
  marginBottom: 24,
  display: "flex",
  alignItems: "center",
  gap: 10,
  fontSize: 13,
  color: "#004578",
};

const errorBoxStyle: React.CSSProperties = {
  background: "#fde7e9",
  border: "1px solid #d13438",
  borderRadius: 4,
  padding: "10px 16px",
  color: "#a4262c",
  fontSize: 13,
};

const sectionHeadingStyle: React.CSSProperties = {
  borderBottom: "2px solid #0078d4",
  paddingBottom: 8,
  color: "#0078d4",
  display: "flex",
  alignItems: "center",
  gap: 10,
};

const countBadgeStyle: React.CSSProperties = {
  background: "#0078d4",
  color: "white",
  borderRadius: 12,
  padding: "1px 10px",
  fontSize: 13,
  fontWeight: 600,
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 14,
  tableLayout: "fixed",
};

const thStyle: React.CSSProperties = {
  padding: "10px 12px",
  fontWeight: 600,
  borderBottom: "1px solid #ddd",
};

const tdStyle: React.CSSProperties = {
  padding: "10px 12px",
  verticalAlign: "middle",
};

const btnStyle: React.CSSProperties = {
  color: "white",
  border: "none",
  padding: "6px 14px",
  fontSize: 13,
  borderRadius: 4,
  cursor: "pointer",
};

export default GroupsRosterPage;
